import React, { useMemo } from 'react';
import { Users } from 'lucide-react';
import NetWorthBadge from '../components/NetWorthBadge';
import { ToolTip } from '../../../components/ToolTip';

const CustomersTab = ({ customers = [], loading = false }) => {
  // Define generation ranges with birth years
  const generationDefinitions = useMemo(() => [
    { name: "Gen Alpha", startYear: 2013, endYear: new Date().getFullYear() },
    { name: "Gen Z", startYear: 1997, endYear: 2012 },
    { name: "Millennial", startYear: 1981, endYear: 1996 },
    { name: "Gen X", startYear: 1965, endYear: 1980 },
    { name: "Baby Boomer", startYear: 1946, endYear: 1964 },
    { name: "Silent Generation", startYear: 1928, endYear: 1945 },
    { name: "Greatest Generation", startYear: 1901, endYear: 1927 }
  ], []);

  // Function to get generation and its details from age
  const getGenerationDetails = useMemo(() => (age) => {
    if (!age || isNaN(parseInt(age, 10))) {
      return { 
        name: "Unknown", 
        birthYear: null, 
        ageRange: "Unknown"
      };
    }
    
    const ageNum = parseInt(age, 10);
    const currentYear = new Date().getFullYear();
    const birthYear = currentYear - ageNum;
    
    // Finds the generation this birth year belongs to
    const generation = generationDefinitions.find(
      gen => birthYear >= gen.startYear && birthYear <= gen.endYear
    ) || { name: "Unknown", startYear: null, endYear: null };
    
    // Calculate age range for this generation
    const youngestAge = currentYear - generation.endYear;
    const oldestAge = currentYear - generation.startYear;
    const ageRange = generation.startYear ? `${youngestAge}-${oldestAge}` : "Unknown";
    
    return {
      name: generation.name,
      birthYear,
      birthYearRange: generation.startYear ? `${generation.startYear}-${generation.endYear}` : "Unknown",
      ageRange
    };
  }, [generationDefinitions]);

  // Map generation to color classes for better maintainability
  const generationColorMap = useMemo(() => ({
    'Gen Z': 'bg-blue-100 text-primaryDark',
    'Millennial': 'bg-green-100 text-green-800',
    'Gen X': 'bg-yellow-100 text-yellow-800',
    'Baby Boomer': 'bg-purple-100 text-purple-800',
    'Gen Alpha': 'bg-pink-100 text-pink-800',
    'Silent Generation': 'bg-indigo-100 text-indigo-800',
    'Greatest Generation': 'bg-red-100 text-red-800',
    'Unknown': 'bg-gray-100 text-gray-800'
  }), []);

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {customers.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Generation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Group
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {customers.map((customer) => {
                const genDetails = getGenerationDetails(customer.age);
                const colorClass = generationColorMap[genDetails.name] || generationColorMap.Unknown;
                
                // Create detailed tooltip content
                const tooltipContent = customer.age ? (
                  <div>
                    <p><strong>{genDetails.name}:</strong> Born {genDetails.birthYearRange}</p>
                  </div>
                ) : "Age: Unknown";
                
                return (
                  <tr key={customer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-custom-blue" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{customer.name}</div>
                          <div className="text-sm text-gray-500">{customer.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <NetWorthBadge netWorth={customer.net_worth} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <ToolTip content={tooltipContent}>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
                          {genDetails.name}
                        </span>
                      </ToolTip>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {customer.groups || 'N/A'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
          <p className="text-gray-500">Try adjusting your search or filter criteria.</p>
        </div>
      )}
    </div>
  );
};

export default CustomersTab;